import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompleteStep } from '../../../lib/Onboarding';
import SquareIcon from '~/components/icons/SquareIcon';
import TStrings from './Payment.i18n';
import PaymentProcessorCard from './PaymentProcessorCard';
import type { PaymentIntegrationProps } from './Payment.types';
import { businessApiRoute, get } from '~/lib/Api';
import { PaymentProcessor } from './Payment.types';
import Modal from '~/components/Modal';
import Loading from '~/components/Loading';
import Select from '~/components/Select';
import {
    setSquareDetailsQuery,
    useBusinessInfo,
    useSquareDetailsQuery,
    useUserInfoQuery,
} from '~/lib/Queries';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import ErrorMessageBlock from '~/components/ErrorMessageBlock';
import useDelete from '~/lib/useDelete';
import * as Toast from '~/lib/Toast';
import CollapsiblePanel from '~/components/CollapsiblePanel';

const SquareDetailsModal = ({ isOpen, onClose, onConnect, setSquareData }) => {
    const [location, setLocation] = useState(null);

    const { data: userInfoData } = useUserInfoQuery();
    const { isLoading, data } = useSquareDetailsQuery();
    const { save, isSaving } = useBusinessPostApi(
        `/integrations/payments/square`,
    );

    const { save: saveDeviceCode, isSaving: isSavingDeviceCode } =
        useBusinessPostApi(`/integrations/payments/square/device-code`);

    const { saveDelete, isDeleting } = useDelete(
        `/integrations/payments/square/disconnect`,
        {
            prompt: 'Are you sure you want to disconnect Square?',
        },
    );

    useEffect(() => {
        setLocation(data?.location);
    }, [data?.location]);

    const locationOptions = useMemo(() => {
        let opts = { '0': 'Choose...' };
        (data?.allLocations || []).forEach(l => (opts[l.id] = l.name));
        return opts;
    }, [data]);

    const hasLocations = Object.keys(locationOptions).length > 1;

    function doSave() {
        save({
            location,
        }).then(result => {
            setSquareDetailsQuery(result);
            onClose();
        });
    }

    function doDisconnect() {
        saveDelete().then(result => {
            setSquareData(result);
            onClose();
            Toast.success('Disconnected from Square');
        });
    }

    function doCreateDeviceCode() {
        saveDeviceCode({
            name: 'Breely Terminal',
        }).then(result => {
            setSquareDetailsQuery(result);
            Toast.success(
                'Successfully created device code. Please use this code within the next 5 minutes to pair terminal with Breely.',
            );
        });
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h2 className="mb-6">Square</h2>
            {isLoading && <Loading />}
            {!isLoading && (
                <div>
                    {hasLocations && (
                        <Select
                            label="Square Location for Payments"
                            options={locationOptions}
                            onChange={value => setLocation(value)}
                            value={location}
                        />
                    )}
                    {!hasLocations && (
                        <ErrorMessageBlock>
                            You have no location in Square that are capable of{' '}
                            processing credit cards. Please check your Square
                            dashboard to enable credit card processing at a
                            location.
                        </ErrorMessageBlock>
                    )}

                    <CollapsiblePanel
                        title="Square Terminal (Optional)"
                        className="mt-4"
                        initialOpen={data?.deviceCodes?.length > 0}
                    >
                        <div className="p-4">
                            <div>
                                Beta feature. Use your Square terminal POS for
                                in person payments.
                            </div>
                            <div className="mt-4">
                                {!data?.deviceCodes?.length && (
                                    <div>
                                        Create a device code to pair your Square
                                        terminal with Breely.
                                    </div>
                                )}
                                {data?.deviceCodes?.length > 0 && (
                                    <>
                                        <div className="">
                                            {data?.deviceCodes?.map(dc => (
                                                <div key={dc.id}>
                                                    Device Code: {dc.code}
                                                    <span className="text-gray-500 ml-2">
                                                        ({dc.status})
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-1 text-gray-500 text-sm">
                                            Unpaired device codes must be used
                                            within 5min to connect. From the
                                            Square terminal log out, then sign
                                            in again using "Device Code".
                                        </div>
                                    </>
                                )}

                                <button
                                    className="btn-light-pill mt-2"
                                    type="button"
                                    onClick={doCreateDeviceCode}
                                >
                                    {isSavingDeviceCode
                                        ? 'Creating...'
                                        : 'Create Device Code'}
                                </button>
                            </div>
                        </div>
                    </CollapsiblePanel>

                    <div className="flex items-center space-x-6 mt-4">
                        <button
                            type="button"
                            className="btn-save"
                            onClick={doSave}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>

                        <button
                            type="button"
                            className="btn-light-pill-lg"
                            onClick={onConnect}
                        >
                            Reconnect
                        </button>

                        <button
                            type="button"
                            className="btn-pink-pill-lg"
                            onClick={doDisconnect}
                        >
                            Disconnect
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

type SquareIntegrationProps = PaymentIntegrationProps;

type SquareEndpointResponse = any; // TODO
const SquareIntegration = ({
    className,
    disabled,
    onIntegrationEnabled,
}: SquareIntegrationProps) => {
    const [squareData, setSquareData] = useState<SquareEndpointResponse | null>( // TODO
        null,
    );
    const { complete } = useCompleteStep();
    const [isModalShowing, setIsModalShowing] = useState(false);

    useEffect(() => {
        get(businessApiRoute(`/integrations/payments/square`)).then(result => {
            console.log('got result', result);
            setSquareData(result);
            if (result?.isConfigured) {
                onIntegrationEnabled(PaymentProcessor.Square);
                complete('integration-payments');
            }
        });
    }, []);

    const connectCallback = useCallback(() => {
        if (squareData?.url) {
            window.location.href = squareData.url;
        }
    }, [squareData?.url]);

    return (
        <>
            <PaymentProcessorCard
                enabled={!disabled || squareData?.isConfigured}
                featured={true}
                buttonText={
                    squareData?.isConfigured
                        ? TStrings.MANAGE_SQUARE_BUTTON
                        : TStrings.CONNECT_SQUARE_BUTTON
                }
                className={className}
                icon={<SquareIcon />}
                description={TStrings.SQUARE_DESCRIPTION}
                onConnectClick={() => {
                    if (squareData?.isConfigured) {
                        setIsModalShowing(true);
                    } else {
                        connectCallback();
                    }
                }}
                hasInit={!!squareData}
                isConnected={squareData?.isConfigured}
                isLoading={!squareData}
                error={squareData?.error}
                subtitle1={
                    squareData?.isConfigured && !squareData?.isChargesEnabled
                        ? TStrings.UNABLE_TO_RECEIVE_PAYMENTS
                        : ''
                }
                subtitle2={
                    squareData?.isConfigured && !!squareData?.areRequirementsDue
                        ? TStrings.SQUARE_STEPS_REQUIRED
                        : ''
                }
            />
            <SquareDetailsModal
                isOpen={isModalShowing}
                onClose={() => setIsModalShowing(false)}
                onConnect={connectCallback}
                setSquareData={setSquareData}
            />
        </>
    );
};

export default SquareIntegration;
